import { forgetReturnLink, rememberReturnLink, takeReturnLink } from '@/utils/returnLink';
import { useMainStore } from '@/store'; // adjust if needed
import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import Dashboard from '../pages/Dashboard.vue';
import { setPageTitle, can } from '@/composables';
import { clearImpersonation, saveImpersonation } from '@/composables/impersonation';
import { forgetTabs } from '@/utils/orderTabs';

const routes: Array<RouteRecordRaw> = [
  {
    path: '/',
    redirect: '/dashboard',
  },
  {
    path: '/delivery-box',
    name: 'delivery-box',
    component: () => import('../pages/DeliveryBox.vue'),
  },
  {
    path: '/default-categories',
    name: 'default-categories',
    component: () => import('../pages/DefaultCategories.vue'),
  },
  {
    path: '/default-products',
    name: 'default-products',
    component: () => import('../pages/DefaultProducts/DefaultProducts.vue'),
  },
  {
    path: '/resellers-management',
    name: 'resellers-management',
    component: () => import('../pages//ResellersManagement/ResellersManagement.vue'),
  },
  {
    path: '/order-barcode',
    name: 'order-barcode',
    component: () => import('../pages/OrderBarcode.vue'),
  },
  {
    path: '/generate-drawing-sheet',
    name: 'generate-drawing-sheet',
    component: { template: '<div></div>' },
    beforeEnter() {
      window.location.href = '/generate-drawing-sheet/index.html';
    },
  },
  {
    path: '/buying-groups-sales-reps',
    name: 'buying-groups-sales-reps',
    component: () => import('@/pages/BuyingGroupSalesRep.vue'),
    // AB#21959 — Kickback & Commission lives in the CRM section.
    meta: { permission: 'view-kickback-commission', allPermissions: ['view-crm'] },
  },
  {
    path: '/finance/mark-invoiced',
    name: 'finance-mark-invoiced',
    component: () => import('@/pages/Finance/MarkInvoiced.vue'),
    // AB#22389 — Finance → Mark orders invoiced: invoices raised outside Stripe.
    // view-finance is not on this tier's backend yet, so the page's own
    // permission is the gate here.
    meta: { permission: 'mark-orders-invoiced' },
  },
  // Settings pilot (AB#20755) — dark launch: routes registered while the
  // sidebar navigation still points at the legacy app. Route paths match
  // the legacy app exactly so deep links survive the cut-over.
  {
    path: '/general-settings',
    name: 'general-settings',
    component: () => import('@/pages/Settings/GeneralSettings.vue'),
  },
  {
    path: '/email-configuration',
    name: 'email-configuration',
    component: () => import('@/pages/Settings/EmailConfiguration.vue'),
  },
  {
    path: '/ui-branding',
    name: 'ui-branding',
    component: () => import('@/pages/Settings/UIBranding.vue'),
  },
  // AB#21836 — Desk messages (senders only; the page and its API refuse without
  // manage-desk-messages). Receivers never come here: they get the card and the bell.
  {
    path: '/desk-messages',
    name: 'desk-messages',
    component: () => import('@/pages/Settings/DeskMessages.vue'),
  },
  // AB#21842 — Ops Command Center: a page for a wall. Bare (no sidebar, no app
  // bar, no launcher) and gated by features.command_center, which the backend
  // grants only to roles holding view-command-center; the API refuses the
  // same users, so a typed URL is never the only gate.
  {
    path: '/command-center',
    name: 'command-center',
    component: () => import('@/pages/CommandCenter.vue'),
    meta: { bare: true, feature: 'command_center' },
  },
  // Price List, SKU & Factories (AB#20756) — dark launch, same discipline as the
  // Settings pilot: routes live here, the sidebar keeps pointing at the legacy
  // app until each path is added to V2_OWNED_PATHS in useMenuHref.ts.
  {
    path: '/price-list',
    name: 'price-list',
    component: () => import('@/pages/PriceList/PriceList.vue'),
    // AB#21970
    meta: { permission: 'view-price-lists' },
  },
  {
    path: '/factories',
    name: 'factories',
    component: () => import('@/pages/Factories/Factories.vue'),
  },
  {
    path: '/sku',
    name: 'sku',
    component: () => import('@/pages/Sku/Sku.vue'),
  },
  // Categories, Design Categories & Brand (AB#20757) — dark launch. Paths match
  // the legacy admin and the live navigation url_path values (/brands,
  // /design-categories); /products/categories is direct-URL only, as in legacy.
  {
    path: '/products/categories',
    name: 'categories',
    component: () => import('@/pages/Categories/Categories.vue'),
  },
  {
    path: '/design-categories',
    name: 'design-categories',
    component: () => import('@/pages/DesignCategories/DesignCategories.vue'),
    // AB#21963
    meta: { permission: 'view-design-categories' },
  },
  {
    path: '/brands',
    name: 'brands',
    component: () => import('@/pages/Brands/Brands.vue'),
  },
  // Translations, Email & Video Templates (AB#20758) — dark launch. Paths match the
  // legacy admin and the navigation url_path values; sidebar cut-over follows QA.
  {
    path: '/translations',
    name: 'translations',
    component: () => import('@/pages/Translations/Translations.vue'),
  },
  {
    path: '/email-templates',
    name: 'email-templates',
    component: () => import('@/pages/EmailTemplates/EmailTemplates.vue'),
  },
  {
    path: '/orders/restore',
    name: 'restore-order',
    component: () => import('@/pages/RestoreOrder/RestoreOrder.vue'),
    meta: { permission: 'restore-order' },
  },
  {
    path: '/general-action',
    name: 'general-action',
    component: () => import('@/pages/GeneralAction/GeneralAction.vue'),
  },
  {
    path: '/video-templates',
    name: 'video-templates',
    component: () => import('@/pages/VideoTemplates/VideoTemplates.vue'),
    props: { kind: 'company' },
  },
  {
    path: '/default-video-templates',
    name: 'default-video-templates',
    component: () => import('@/pages/VideoTemplates/VideoTemplates.vue'),
    props: { kind: 'default' },
  },
  {
    path: '/video-usage',
    name: 'video-usage',
    component: () => import('@/pages/VideoUsage/VideoUsage.vue'),
  },
  {
    path: '/products/data-source',
    name: 'data-source',
    component: () => import('@/pages/DataSource/DataSource.vue'),
  },
  {
    // Tracking Master Dashboard (US #18826, task 18973). Sidebar visibility is
    // role-gated (Logistics/CSM) in the store; this route is lazy-loaded.
    path: '/tracking',
    name: 'tracking',
    component: () => import('../pages/TrackingMaster.vue'),
    // Role-gated (task 18973): the beforeEach guard 403s direct-URL access
    // unless the user's role carries the view-tracking-dashboard permission.
    meta: { permission: 'view-tracking-dashboard' },
  },
  {
    // Standalone order-type change page (US #20569). Its own URL so an Order
    // Administrator can be given just this: move an order between New order,
    // Reorder and Remake, and fill the remake intake questionnaire. The optional
    // :orderNo segment lets the order detail deep-link straight into it.
    path: '/order-type-change/:orderNo?',
    name: 'order-type-change',
    component: () => import('@/pages/OrderTypeChange.vue'),
    // The guard below 403s a typed URL without the permission. The API route and
    // the service check it again; a hidden page is never the only gate.
    meta: { permission: 'change-order-type' },
  },
  {
    // Needs Attention queue (AB#21587). The proactive counterpart to Ask ELSA:
    // the ranked list of orders that need the person today. Gated by
    // view-needs-attention — the guard 403s a typed URL without it, and the API
    // routes check the same permission, so a hidden page is never the only gate.
    // The team overview lives on the same page, shown only to a super admin
    // (view-team-needs-attention), decided inside the page.
    path: '/needs-attention',
    name: 'needs-attention',
    component: () => import('@/pages/NeedsAttention.vue'),
    // AB#21997 — or the Financial Controllers' queue or overview; the page
    // shows whichever of the four views the person holds.
    meta: {
      anyPermission: [
        'view-needs-attention',
        'view-finance-needs-attention',
        'view-finance-team-needs-attention',
      ],
    },
  },
  {
    // AB#21902 — Accounts: the CSM's home page. One row per company they may
    // see, then one page per company. view-account-health at the guard and
    // on the API; a CSM's scope is decided server-side.
    path: '/accounts',
    name: 'accounts',
    component: () => import('@/pages/Accounts/Accounts.vue'),
    meta: { permission: 'view-account-health', allPermissions: ['view-crm'] },
  },
  {
    // AB#21914 — Edit tech pack, V2. The V1 page (admin.custimoo.com
    // /edit-techpack/:id) stays reachable as "Classic view" from the page and
    // from the order actions' split button.
    path: '/edit-techpack/:orderId(\\d+)',
    name: 'edit-techpack',
    component: () => import('@/pages/TechPack/EditTechPackPage.vue'),
    meta: { permission: 'update-techpack' },
  },
  {
    // AB#21971 — CRM → Quotes: the quote pipeline, read live from the order
    // tables. A CRM page: view-crm-quotes plus the section's view-crm.
    path: '/crm/quotes',
    name: 'crm-quotes',
    component: () => import('@/pages/Crm/Quotes.vue'),
    meta: { permission: 'view-crm-quotes', allPermissions: ['view-crm'] },
  },
  {
    path: '/accounts/:companyId(\\d+)',
    name: 'account',
    component: () => import('@/pages/Accounts/AccountHealth.vue'),
    meta: { permission: 'view-account-health', allPermissions: ['view-crm'] },
  },
  {
    // AB#21903 — Team: the lead order administrator's home. One tab per
    // permission (Flagged, Load, Mood); any one of them opens the page, and
    // each tab's API is gated by its own permission.
    path: '/team',
    name: 'team',
    component: () => import('@/pages/Team/Team.vue'),
    meta: {
      anyPermission: [
        'view-team-needs-attention',
        // AB#21997 — the Finance team overview is a team of its own on this page.
        'view-finance-team-needs-attention',
        'view-team-load',
        'view-team-communication-mood',
        'view-forecast',
      ],
    },
  },
  {
    // AB#22158 — Setup → Copy lockers & shops between customer accounts.
    path: '/setup/copy-lockers-shops',
    name: 'account-copy',
    component: () => import('@/pages/Setup/AccountCopy.vue'),
    meta: { permission: 'copy-customer-content' },
  },
  {
    // AB#21904 / AB#21908 — Setup → Agents: ELSA and ATLAS, one tab per
    // permission; any one of them opens the page.
    path: '/setup/agents/:agent(elsa|atlas)?',
    name: 'agents-setup',
    component: () => import('@/pages/Setup/AgentsSetup.vue'),
    meta: {
      anyPermission: [
        'view-elsa-quality',
        'view-elsa-outcomes',
        'manage-elsa-settings',
        'view-atlas-quality',
        'manage-atlas-settings',
      ],
    },
  },
  {
    // The address the ELSA page had before AB#21908.
    path: '/setup/elsa',
    redirect: { name: 'agents-setup', params: { agent: 'elsa' } },
  },
  {
    // 403 shown when a user hits a permission-restricted page they can't access
    // (task 18973). Reachable regardless of permission.
    path: '/403',
    name: 'not-authorized',
    component: () => import('@/pages/NotAuthorized.vue'),
  },
  {
    path: '/roles',
    name: 'roles',
    component: () => import('@/pages/Roles/Roles.vue'),
  },
  // AB#20740 — Logistics / Shipping dashboard. The three shipping permissions are
  // cumulative (operate includes view, manage includes both). The guard hands
  // meta.permission to can(), which reads an array as "any of": the read pages
  // open for any shipping permission; Reconcile, Audit log and Settings need
  // manage-shipping. The API refuses the same users (routes/adminShippingApi.php).
  { path: '/shipping', redirect: '/shipping/queue' },
  {
    path: '/shipping/queue',
    name: 'shipping-queue',
    component: () => import('@/pages/Shipping/ShippingQueue.vue'),
    meta: { permission: ['view-shipping', 'operate-shipping', 'manage-shipping'] },
  },
  {
    path: '/shipping/shipments',
    name: 'shipping-shipments',
    component: () => import('@/pages/Shipping/ShippingShipments.vue'),
    meta: { permission: ['view-shipping', 'operate-shipping', 'manage-shipping'] },
  },
  // feel-queue — no Tracking tab (the old tool had none, and its "0 delivered"
  // view was empty by construction): old links land on Shipments.
  { path: '/shipping/tracking', name: 'shipping-tracking', redirect: '/shipping/shipments' },
  {
    // AB#22139
    path: '/shipping/pickups',
    name: 'shipping-pickups',
    component: () => import('@/pages/Shipping/ShippingPickups.vue'),
    meta: { permission: ['view-shipping', 'operate-shipping', 'manage-shipping'] },
  },
  {
    // AB#22149
    path: '/shipping/efs',
    name: 'shipping-efs',
    component: () => import('@/pages/Shipping/ShippingEfs.vue'),
    meta: { permission: ['view-shipping', 'operate-shipping', 'manage-shipping'] },
  },
  {
    // Parity with the live logistics tool: the rules engine and the product library.
    path: '/shipping/rules',
    name: 'shipping-rules',
    component: () => import('@/pages/Shipping/ShippingRules.vue'),
    meta: { anyPermission: ['view-shipping', 'operate-shipping', 'manage-shipping'] },
  },
  {
    path: '/shipping/products',
    name: 'shipping-products',
    component: () => import('@/pages/Shipping/ShippingProducts.vue'),
    meta: { anyPermission: ['view-shipping', 'operate-shipping', 'manage-shipping'] },
  },
  {
    path: '/shipping/exceptions',
    name: 'shipping-exceptions',
    component: () => import('@/pages/Shipping/ShippingExceptions.vue'),
    meta: { permission: ['view-shipping', 'operate-shipping', 'manage-shipping'] },
  },
  {
    path: '/shipping/abandoned',
    name: 'shipping-abandoned',
    component: () => import('@/pages/Shipping/ShippingAbandoned.vue'),
    meta: { permission: ['view-shipping', 'operate-shipping', 'manage-shipping'] },
  },
  {
    path: '/shipping/deleted',
    name: 'shipping-deleted',
    component: () => import('@/pages/Shipping/ShippingDeleted.vue'),
    meta: { permission: ['view-shipping', 'operate-shipping', 'manage-shipping'] },
  },
  {
    path: '/shipping/addresses',
    name: 'shipping-addresses',
    component: () => import('@/pages/Shipping/ShippingAddresses.vue'),
    meta: { permission: ['view-shipping', 'operate-shipping', 'manage-shipping'] },
  },
  {
    path: '/shipping/reconcile',
    name: 'shipping-reconcile',
    component: () => import('@/pages/Shipping/ShippingReconcile.vue'),
    meta: { permission: 'manage-shipping' },
  },
  {
    path: '/shipping/audit',
    name: 'shipping-audit',
    component: () => import('@/pages/Shipping/ShippingAudit.vue'),
    meta: { permission: 'manage-shipping' },
  },
  {
    path: '/shipping/settings',
    name: 'shipping-settings',
    component: () => import('@/pages/Shipping/ShippingSettings.vue'),
    meta: { permission: 'manage-shipping' },
  },
  {
    // AB#22077 — ACL → Audit log; the API refuses the same users.
    path: '/audit-log',
    name: 'audit-log',
    component: () => import('@/pages/AuditLog/AuditLog.vue'),
    meta: { permission: 'view-audit-log' },
  },
  {
    // AB#22092 — Product development → Developments; the API refuses the same users.
    path: '/product-development',
    name: 'product-development',
    component: () => import('@/pages/ProductDevelopment/ProductDevelopment.vue'),
    meta: { permission: 'view-product-development' },
  },
  {
    // AB#22092 — phase 2b: Product development → Insights (cycle times, late rate, stalls).
    path: '/product-development/insights',
    name: 'product-development-insights',
    component: () => import('@/pages/ProductDevelopment/DevelopmentInsights.vue'),
    meta: { permission: 'view-product-development' },
  },
  {
    // AB#22092 — Setup → Product development (checklists, days per step).
    path: '/product-development/setup',
    name: 'product-development-setup',
    component: () => import('@/pages/ProductDevelopment/DevelopmentSettings.vue'),
    meta: { permission: 'manage-development-settings' },
  },
  {
    path: '/permissions',
    name: 'permissions',
    component: () => import('@/pages/Permissions/Permissions.vue'),
  },
  {
    path: '/companies',
    name: 'companies',
    component: () => import('@/pages/Companies/Companies.vue'),
  },
  {
    path: '/users',
    name: 'users',
    component: () => import('@/pages/Users/Users.vue'),
  },
  {
    path: '/customers',
    name: 'customers',
    component: () => import('@/pages/Customers/Customers.vue'),
  },
  {
    // Standalone order-type change page (US #20569). Its own URL so an Order
    // Administrator can be given just this: move an order between New order,
    // Reorder and Remake, and fill the remake intake questionnaire. The optional
    // :orderNo segment lets the order detail deep-link straight into it.
    //
    // NOTE: `meta.permission` is only enforced where router/index.ts carries the
    // beforeEach guard. This branch has none, so the page checks the permission
    // itself as well — see OrderTypeChange.vue.
    path: '/order-type-change/:orderNo?',
    name: 'order-type-change',
    component: () => import('@/pages/OrderTypeChange.vue'),
    meta: { permission: 'change-order-type' },
  },

  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    redirect: '/dashboard',
  },
  {
    path: '/dashboard',
    name: 'dashboard',
    component: Dashboard,
  },
  {
    // AB#22512 — the Dashboard's audience: access-new-dashboard, checked at sign-in
    // as for the Dashboard; the list's API route (consolidation-waiting-index) is
    // gated on the server. On this line the server sends no features.order_listing
    // and the Dashboard has no feature gate, so neither has this page (a
    // meta.feature here would refuse everyone).
    path: '/orders/waiting-for-consolidation',
    name: 'consolidation-waiting',
    component: () => import('@/pages/ConsolidationWaiting.vue'),
  },
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior: () => ({ top: 0 }),
  routes,
});

let isUserInitialized = false;

router.beforeEach(async (to, from, next) => {
  const store = useMainStore();
  const queryToken = to.query.access_token as string | undefined;
  setPageTitle();

  if (queryToken) {
    // Save token and remove from URL
    localStorage.setItem('access_token', queryToken);

    // A token handoff means a (possibly different) identity has just signed in,
    // so the cached user must be re-fetched. The redirect below is client-side,
    // which leaves this module — and therefore isUserInitialized — intact; with
    // the flag still true the guard would skip getCurrentUser() and keep the
    // PREVIOUS user's role while holding the NEW user's token. A merchant then
    // renders the staff dashboard shell (#19956). Logout never hit this because
    // it does a full window.location reload.
    isUserInitialized = false;

    // Whoever is signing in, the orders open in the pane were the previous
    // person's. An admin starting an impersonation must land on the merchant's
    // dashboard, not on their own five open orders — and the same on the way
    // back. The set is also owner-checked on read (utils/orderTabs), so this
    // is belt and braces for the one moment we know the identity changed.
    forgetTabs(localStorage);

    // Impersonation context passed along by the legacy admin handoff.
    // A handoff WITHOUT the flag is a normal session and clears any old flag.
    if (String(to.query.impersonated || '') === '1') {
      saveImpersonation({
        by: {
          name: String(to.query.imp_by_name || ''),
          email: String(to.query.imp_by_email || ''),
          role: String(to.query.imp_by_role || ''),
        },
        started_at: String(to.query.imp_started_at || ''),
      });
      // An impersonation lands on that merchant's dashboard, never on a link
      // the admin was opening for themselves (AB#21966).
      forgetReturnLink();
    } else {
      clearImpersonation();
    }

    // AB#21594 — a token handoff is a fresh sign-in (or an impersonation
    // start): the one moment "land on the queue" should apply. Mark it so the
    // next guard pass (after permissions load) can send a holder of
    // view-needs-attention to their queue instead of the orders table. Set only
    // on the handoff, so a later refresh of the dashboard is never hijacked.
    try {
      sessionStorage.setItem('na_pending_landing', '1');
    } catch {
      // sessionStorage can be unavailable; the landing redirect just won't fire.
    }

    // Strip the token + impersonation params from the visible URL.
    const {
      access_token: _a,
      refresh_token: _r,
      impersonated: _imp,
      imp_by_name: _ibn,
      imp_by_email: _ibe,
      imp_by_role: _ibr,
      imp_started_at: _isa,
      ...cleanQuery
    } = to.query;

    // Redirect to same path without token in query
    return next({
      path: to.path,
      query: cleanQuery,
      replace: true,
    });
  }

  const token = localStorage.getItem('access_token');

  // AB#21966 — signed out and opening a link (the team chat email's
  // "Open the chat and reply"): remember it. The first request answers 401 and
  // sends them to the login; after signing in they are brought back here.
  if (!token) {
    rememberReturnLink(to.fullPath);
  }

  if (token && !isUserInitialized) {
    try {
      await store.getPermissions();
      await store.getNavigation();
      await store.getCurrentUser();

      isUserInitialized = true;

      if (!store.permissions.includes('access-new-dashboard')) {
        await store.logout();
        return;
      }
    } catch (error) {
      console.error('Error loading user data:', error);
      await store.logout();
      return;
    }
  }

  // AB#21594 — land a fresh sign-in on the Needs Attention queue when the
  // person holds view-needs-attention. Only fires on the pass right after a
  // token handoff (the flag), only when the destination is the default landing
  // (the dashboard), and only once — so clicking Dashboard later, or refreshing
  // it, is left alone. A person without the permission falls through to the
  // dashboard exactly as before.
  try {
    if (sessionStorage.getItem('na_pending_landing') === '1') {
      sessionStorage.removeItem('na_pending_landing');
      // AB#21966 — they signed in because a link sent them to the login:
      // finish opening that link instead of the default landing. Only when the
      // handoff itself landed on the plain dashboard, and only for a page that
      // exists; the permission gate below still applies on the next pass.
      const back = takeReturnLink();
      if (back && to.name === 'dashboard' && Object.keys(to.query).length === 0) {
        const target = router.resolve(back);
        if (target.matched.length > 0 && target.name !== 'not-authorized') {
          return next({ path: target.path, query: target.query, hash: target.hash, replace: true });
        }
      }
      if (
        to.name === 'dashboard' &&
        (can('view-needs-attention') || can('view-finance-needs-attention'))
      ) {
        return next({ name: 'needs-attention', replace: true });
      }
    }
  } catch {
    // sessionStorage unavailable — skip the landing redirect.
  }

  // Per-route permission gate (task 18973): block direct-URL access to a
  // permission-restricted page (e.g. /tracking) when the user's role lacks the
  // required permission. Hidden sidebar entries alone don't stop a typed URL.
  const requiredPermission = to.meta.permission as string | undefined;
  if (requiredPermission && !can(requiredPermission)) {
    return next({ name: 'not-authorized' });
  }
  // AB#21951 — a page inside a gated section (CRM) also needs the section's permission.
  const allPermissions = to.meta.allPermissions as string[] | undefined;
  if (allPermissions && !allPermissions.every((p) => can(p))) {
    return next({ name: 'not-authorized' });
  }
  // AB#21903 — a page made of per-permission tabs needs any one of them.
  const anyPermission = to.meta.anyPermission as string[] | undefined;
  if (anyPermission && !anyPermission.some((p) => can(p))) {
    return next({ name: 'not-authorized' });
  }
  const requiredFeature = to.meta.feature as string | undefined;
  if (
    requiredFeature &&
    !(store.features as Record<string, boolean> | undefined)?.[requiredFeature]
  ) {
    return next({ name: 'not-authorized' });
  }

  next();
});

export default router;
